import { deepClone } from '@direct-frontend/stdlib';

import { getSelector } from './attribution/getSelector';

// Метрика скролла
type ScrollMetric = {
    // Задержка во времени при скролле, в миллисекундах
    time: number;
    // Дебаг информация
    attribution: {
        // Селектор элемента, на котором сработал скролл
        selector: string;
    };
};

// Обсервер событий скролла на странице по подсчету задержки выполнения JS
type ScrollJsObserver = {
    // Метод для отписки от обработки событий скролла
    disconnect: () => void;
};

export type ScrollObserver = ReturnType<typeof createScrollObserver>;

type IsEntryValidCallback = (entry: PerformanceEntry) => boolean;

export function createScrollObserver(isEntryValid: IsEntryValidCallback) {
    // Время, в течение которого следующее событие скролла считается связанным с предыдущим
    const SCROLL_TIMEOUT = 500; // ms

    // Актуальные значения метрик скролла
    let currentScrollJsMetric: ScrollMetric | undefined;
    let currentScrollLatencyMetric: ScrollMetric | undefined;

    // Времена задержки текущего скролла
    const currentScrollJsTimes: number[] = [];
    const currentScrollLatencyTimes: number[] = [];

    // ID raf по замеру времени от события скролла до след. кадра
    let nextAnimationFrameId: number | undefined;
    // ID таймера по сохранению данных скролла
    let saveScrollDataTimeout: number | undefined;
    // ID таймера по детекту завершения скролла
    let waitEndOfScrollTimeout: number | undefined;

    // Информация об элементе, на котором сработал скролл
    let currentScrollTarget: EventTarget | null | undefined;
    let currentScrollTargetSelector: string | undefined;

    /**
     * Сбрасывает собранные данные о событиях скролла (например, перед сбором новых).
     */
    function resetState() {
        currentScrollJsTimes.length = 0;
        currentScrollLatencyTimes.length = 0;

        if (typeof nextAnimationFrameId === 'number') {
            window.cancelAnimationFrame(nextAnimationFrameId);
            nextAnimationFrameId = undefined;
        }

        if (typeof saveScrollDataTimeout === 'number') {
            window.clearTimeout(saveScrollDataTimeout);
            saveScrollDataTimeout = undefined;
        }

        if (typeof waitEndOfScrollTimeout === 'number') {
            window.clearTimeout(waitEndOfScrollTimeout);
            waitEndOfScrollTimeout = undefined;
        }

        currentScrollTarget = undefined;
        currentScrollTargetSelector = undefined;
    }

    /**
     * Считает среднеквадратичное всех задержек скролла.
     */
    function calculateScrollTime(scrollTimes: number[]) {
        const scrollTimesSquaresSum = scrollTimes.reduce((result, scrollTime) => {
            result += scrollTime * scrollTime;

            return result;
        }, 0);

        return Math.sqrt(scrollTimesSquaresSum / scrollTimes.length);
    }

    /**
     * Возвращает новое максимальное значение метрики задержки скролла, если:
     * - Это первый раз, когда мы рассчитываем метрику.
     * - Новое значение больше, чем уже существующее.
     */
    function getNewScrollMetric(scrollTimes: number[], scrollPreviousTime?: number) {
        if (!scrollTimes.length) {
            return;
        }

        const currentScrollTime = calculateScrollTime(scrollTimes);
        const isNoPreviousScrollTime = !scrollPreviousTime;
        const isCurrentScrollTimeOver = scrollPreviousTime && currentScrollTime > scrollPreviousTime;

        if (isNoPreviousScrollTime || isCurrentScrollTimeOver) {
            return {
                time: currentScrollTime,
                attribution: {
                    selector: currentScrollTargetSelector ?? 'unknown',
                },
            };
        }

        return;
    }

    /**
     * Рассчитывает значение всех метрик скролла: задержка JS и задержка отрисовки.
     */
    function calculateAndResetScrollMetrics() {
        const newScrollJsMetric = getNewScrollMetric(currentScrollJsTimes, currentScrollJsMetric?.time);
        if (newScrollJsMetric) {
            currentScrollJsMetric = newScrollJsMetric;
        }

        const newScrollLatencyMetric = getNewScrollMetric(currentScrollLatencyTimes, currentScrollLatencyMetric?.time);
        if (newScrollLatencyMetric) {
            currentScrollLatencyMetric = newScrollLatencyMetric;
        }

        resetState();
    }

    /**
     * Проставляет данные о текущем элементе, на котором сработал скролл.
     */
    function setCurrentScrollTarget(target: EventTarget | null) {
        currentScrollTarget = target;

        if (target instanceof HTMLElement) {
            currentScrollTargetSelector = getSelector(target);
        }
    }

    /**
     * Ожидает завершения скролла (если событие скролла не триггерилось в течение SCROLL_TIMEOUT),
     * и считает метрики скролла.
     */
    function waitEndOfScroll() {
        window.clearTimeout(waitEndOfScrollTimeout);
        waitEndOfScrollTimeout = window.setTimeout(calculateAndResetScrollMetrics, SCROLL_TIMEOUT);
    }

    /**
     * Сохраняет данные о скролле в следующем тике Event Loop.
     */
    function saveScrollData(data: { target: EventTarget | null; scrollEventTime: number; nextFrameTime: number }) {
        saveScrollDataTimeout = window.setTimeout(function saveScrollDataHandler() {
            const { target, scrollEventTime, nextFrameTime } = data;

            // Если нет элемента, на котором в данный момент обрабытывается скролл — проставляем его
            // Если элемент есть — проверяем, что он не отличается
            // Если элемент новый — считаем метрики и сбрасываем состояние для нового сбора данных
            if (currentScrollTarget) {
                if (target !== currentScrollTarget) {
                    calculateAndResetScrollMetrics();
                    setCurrentScrollTarget(target);
                }
            } else {
                setCurrentScrollTarget(target);
            }

            currentScrollJsTimes.push(nextFrameTime - scrollEventTime);

            waitEndOfScroll();
        });
    }

    /**
     * Обработчик событий задержки JS при событиях скролла.
     */
    function scrollJsEventListener(event: Event) {
        const target = event.target;
        const scrollEventTime = performance.now();

        nextAnimationFrameId = window.requestAnimationFrame(function nextAnimationFrameCallback() {
            const nextFrameTime = performance.now();

            saveScrollData({ target, scrollEventTime, nextFrameTime });
        });
    }

    /**
     * Создает обсервер событий скролла на странице по подсчету задержки выполнения JS.
     */
    function createScrollJsObserver() {
        document.addEventListener('scroll', scrollJsEventListener, true);

        return {
            disconnect: function disconnectScrollJsObserver() {
                document.removeEventListener('scroll', scrollJsEventListener, true);
            },
        };
    }

    /**
     * Обработчик событий задержки отрисовки при событиях скролла.
     */
    function scrollLatencyEventListener(event: PerformanceObserverEntryList) {
        event.getEntries().forEach(entry => {
            // Фильтруем невалидные записи (например, попавшие в фон или сгенерированные во время скрытия страницы)
            if (!isEntryValid(entry)) {
                return;
            }

            currentScrollLatencyTimes.push(entry.duration);

            waitEndOfScroll();
        });
    }

    /**
     * Создает обсервер событий скролла на странице по подсчету задержки отрисовки.
     */
    function createScrollLatencyObserver() {
        const supportedEntryTypes = window.PerformanceObserver?.supportedEntryTypes ?? [];
        const isScrollLatencySupported = supportedEntryTypes.includes('scrollUpdateLatency');

        if (!isScrollLatencySupported) {
            return;
        }

        const scrollLatencyObserver = new PerformanceObserver(scrollLatencyEventListener);
        scrollLatencyObserver.observe({ type: 'scrollUpdateLatency', buffered: true });

        return scrollLatencyObserver;
    }

    let scrollJsObserver: ScrollJsObserver | undefined;
    let scrollLatencyObserver: PerformanceObserver | undefined;

    return {
        observe() {
            scrollJsObserver = createScrollJsObserver();
            scrollLatencyObserver = createScrollLatencyObserver();
        },
        getScrollJsMetric() {
            return currentScrollJsMetric && deepClone(currentScrollJsMetric);
        },
        getScrollLatencyMetric() {
            return currentScrollLatencyMetric && deepClone(currentScrollLatencyMetric);
        },
        reset() {
            resetState();

            currentScrollJsMetric = undefined;
            currentScrollLatencyMetric = undefined;
        },
        disconnect() {
            scrollJsObserver?.disconnect();
            scrollLatencyObserver?.disconnect();
            this.reset();
        },
    };
}
